// ============================================
// ONGLET « ARCHIVES » — les dossiers fermés
// ============================================
// Demande de Nathan du 19/10/2026. Les dossiers fermés (CLOSED), du plus
// récemment fermé au plus ancien, avec recherche (n° de dossier, titre,
// client, e-mail, n° de série : la recherche du serveur), période de
// fermeture et pagination. Tableau sur ordinateur, cartes sur téléphone et
// tablette. Un clic ouvre la fiche ; « Désarchiver » (un dossier ou une
// sélection) le repasse en « Réouvert ».

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Archive, ArchiveRestore, ChevronLeft, ChevronRight, Loader2, Search, X, AlertCircle, CheckCircle } from 'lucide-react';
import { AdminApi } from '../../services/api';
import type { Ticket } from '../../types';
import { Button, Card, Input, Select } from '../../shared/ui';
import { useEcranEtroit } from '../../shared/responsive';
import { formatDateTime } from '../../shared/format';
import { BadgeStatut } from '../../shared/ui/BadgeStatut';
import {
  CHARGE_DESARCHIVAGE, ETAT_ARCHIVES_PAR_DEFAUT, PERIODES_ARCHIVES, construireFiltresArchives, dateFermeture,
  type EtatArchives, type PeriodeArchives,
} from './archives';
import { BadgeHorsSav, FenetreDesarchivage } from './Archivage';

const TACTILE = 'max-lg:min-h-11';

const nomClient = (t: Ticket) => t.customer?.displayName || t.companyName || t.contactName || '';
const emailClient = (t: Ticket) => t.customer?.email || t.contactEmail || '';
const reference = (t: Ticket) => t.ticketRef || (t.ticketNumber !== undefined ? String(t.ticketNumber) : t.id);

export const ArchivesView: React.FC<{
  onOuvrirDossier: (id: string) => void;
  /** Incrémenté quand un dossier change ailleurs : la page se relit sans clignoter. */
  version?: number;
  /** État gardé au retour d'une fiche (recherche, période, page). */
  memoire?: React.MutableRefObject<EtatArchives | null>;
  /** Après un désarchivage (compteurs du menu, tableau de bord). */
  surChangement?: () => void | Promise<void>;
  /** Lien « Dossiers » de l'en-tête. */
  onVoirDossiers?: () => void;
}> = ({ onOuvrirDossier, version = 0, memoire, surChangement, onVoirDossiers }) => {
  const [etat, setEtatBrut] = useState<EtatArchives>(() => memoire?.current ?? ETAT_ARCHIVES_PAR_DEFAUT);
  const [saisie, setSaisie] = useState(etat.recherche);
  const [page, setPageDossiers] = useState<{ data: Ticket[]; total: number; totalPages: number }>({ data: [], total: 0, totalPages: 0 });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  // Dossiers à désarchiver (fenêtre ouverte), et résumé de la dernière action.
  const [aDesarchiver, setADesarchiver] = useState<string[] | null>(null);
  const [resultat, setResultat] = useState<{ ok: number; ko: number; erreur?: string } | null>(null);
  const etroit = useEcranEtroit();

  const setEtat = useCallback((maj: Partial<EtatArchives>) => {
    setEtatBrut(e => {
      const suivant = { ...e, ...maj };
      if (memoire) memoire.current = suivant;
      return suivant;
    });
  }, [memoire]);

  // Recherche envoyée au serveur 300 ms après la dernière frappe.
  useEffect(() => {
    if (saisie === etat.recherche) return;
    const minuteur = setTimeout(() => setEtat({ recherche: saisie, page: 1 }), 300);
    return () => clearTimeout(minuteur);
  }, [saisie, etat.recherche, setEtat]);

  const numero = useRef(0);
  const charger = useCallback(async (silencieux: boolean) => {
    const n = ++numero.current;
    if (!silencieux) setChargement(true);
    try {
      const r = await AdminApi.getTickets(construireFiltresArchives(etat));
      if (n !== numero.current) return;
      setPageDossiers({ data: r.data, total: r.total, totalPages: r.totalPages });
      setErreur(null);
      // Page devenue vide (dossiers désarchivés entre-temps) : on recule.
      if (r.data.length === 0 && etat.page > 1 && r.totalPages > 0 && etat.page > r.totalPages) setEtat({ page: r.totalPages });
    } catch (e) {
      if (n !== numero.current) return;
      setErreur(e instanceof Error ? e.message : 'Les archives n’ont pas pu être chargées');
    } finally {
      if (n === numero.current) setChargement(false);
    }
  }, [etat, setEtat]);

  useEffect(() => { void charger(false); }, [charger]);
  const premiereVersion = useRef(true);
  useEffect(() => {
    if (premiereVersion.current) { premiereVersion.current = false; return; }
    void charger(true);
  }, [version]);

  // La sélection ne garde que les dossiers de la page affichée.
  useEffect(() => {
    setSelection(s => {
      const ids = new Set(page.data.map(t => t.id));
      const garde = new Set([...s].filter(id => ids.has(id)));
      return garde.size === s.size ? s : garde;
    });
  }, [page.data]);

  const basculer = (id: string) => setSelection(s => {
    const suivant = new Set(s);
    if (suivant.has(id)) suivant.delete(id); else suivant.add(id);
    return suivant;
  });
  const toutSelectionne = page.data.length > 0 && page.data.every(t => selection.has(t.id));
  const basculerTout = () => setSelection(toutSelectionne ? new Set() : new Set(page.data.map(t => t.id)));

  // Désarchivage : chaque dossier séparément ; les échecs restent sélectionnés.
  const desarchiver = async (ids: string[]) => {
    const resultats = await Promise.allSettled(ids.map(id => AdminApi.updateTicket(id, CHARGE_DESARCHIVAGE)));
    const echecs = ids.filter((_, i) => resultats[i].status === 'rejected');
    const premiere = resultats.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (ids.length === 1 && premiere) {
      // Un seul dossier : l'erreur s'affiche dans la fenêtre, qui reste ouverte.
      throw premiere.reason instanceof Error ? premiere.reason : new Error('Le désarchivage a échoué.');
    }
    setResultat({ ok: ids.length - echecs.length, ko: echecs.length, erreur: premiere?.reason instanceof Error ? premiere.reason.message : undefined });
    setSelection(new Set(echecs));
    await charger(true);
    await surChangement?.();
  };

  const debutAffiche = page.total === 0 ? 0 : (etat.page - 1) * etat.parPage + 1;
  const finAffichee = Math.min(etat.page * etat.parPage, page.total);
  const rechercheActive = etat.recherche.trim() !== '' || etat.periode !== 'tout';

  const boutonDesarchiver = (t: Ticket, classe = '') => (
    <Button variant="secondary" size="sm" onClick={() => setADesarchiver([t.id])}
      aria-label={`Désarchiver le dossier ${reference(t)}`} title="Repasser le dossier en « Réouvert »" className={`${TACTILE} ${classe}`}>
      <ArchiveRestore className="w-4 h-4 mr-1" aria-hidden="true" />Désarchiver
    </Button>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 max-sm:p-3 border-b border-trait bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-encre flex items-center gap-2">
              <Archive className="w-5 h-5 text-graphite" aria-hidden="true" />Archives
            </h1>
            <p className="text-sm text-graphite">
              <span className="tnum">{page.total}</span> dossier{page.total > 1 ? 's' : ''} fermé{page.total > 1 ? 's' : ''}.
              {' '}Rien n’est supprimé : le client garde ses dossiers, et son e-mail sur un dossier fermé le rouvre.
            </p>
          </div>
          {onVoirDossiers && (
            <Button variant="ghost" size="sm" onClick={onVoirDossiers} className={TACTILE}>
              <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" />Dossiers en cours
            </Button>
          )}
        </div>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex-1 min-w-0 sm:max-w-md">
            <Input
              placeholder="N° de dossier, titre, client, e-mail, n° de série…"
              value={saisie}
              onChange={e => setSaisie(e.target.value)}
              icon={<Search className="w-4 h-4" />}
              aria-label="Rechercher dans les archives"
              autoComplete="off"
              className={TACTILE}
            />
          </div>
          <div className="sm:w-56">
            <Select
              aria-label="Période de fermeture"
              value={etat.periode}
              onChange={e => setEtat({ periode: e.target.value as PeriodeArchives, page: 1 })}
              options={PERIODES_ARCHIVES}
              className={TACTILE}
            />
          </div>
          {rechercheActive && (
            <Button variant="ghost" size="sm" onClick={() => { setSaisie(''); setEtat({ recherche: '', periode: 'tout', page: 1 }); }} className={TACTILE}>
              <X className="w-4 h-4 mr-1" aria-hidden="true" />Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {resultat && (
        <div role="status" className={`mx-4 mt-2 p-3 rounded-lg border flex items-start gap-2 text-sm ${resultat.ko > 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
          {resultat.ko > 0 ? <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          <span className="flex-1">
            Désarchivage : {resultat.ok} réussi{resultat.ok > 1 ? 's' : ''}, {resultat.ko} échec{resultat.ko > 1 ? 's' : ''}.
            {resultat.ko > 0 && ` Les dossiers en échec restent sélectionnés.${resultat.erreur ? ` Première erreur : ${resultat.erreur}` : ''}`}
          </span>
          <button onClick={() => setResultat(null)} aria-label="Masquer le résumé" className="opacity-70 hover:opacity-100 inline-flex items-center justify-center max-lg:min-h-11 max-lg:min-w-11"><X className="w-4 h-4" /></button>
        </div>
      )}

      {selection.size > 0 && (
        <div className="mx-4 mt-2 max-sm:mx-3 p-3 bg-blue-600 rounded-lg shadow-flottant flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium text-white">{selection.size} dossier{selection.size > 1 ? 's' : ''} sélectionné{selection.size > 1 ? 's' : ''}</span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setADesarchiver([...selection])} className={`bg-white/10 text-white border-white/20 hover:bg-white/20 ${TACTILE}`}>
              <ArchiveRestore className="w-4 h-4 mr-1" aria-hidden="true" />Désarchiver
            </Button>
            <button onClick={() => setSelection(new Set())} aria-label="Annuler la sélection"
              className="p-1.5 hover:bg-white/20 rounded-full inline-flex items-center justify-center max-lg:min-h-11 max-lg:min-w-11">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto p-4 max-sm:p-3">
        <Card>
          {erreur ? (
            <div role="alert" className="p-6 text-sm text-red-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="flex-1">Les archives n’ont pas pu être chargées : {erreur}</span>
              <Button variant="secondary" size="sm" onClick={() => void charger(false)}>Réessayer</Button>
            </div>
          ) : chargement && page.data.length === 0 ? (
            <div className="p-8 flex items-center justify-center gap-2 text-sm text-graphite"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Chargement des archives…</div>
          ) : page.data.length === 0 ? (
            <div className="p-8 text-center">
              <Archive className="w-8 h-8 mx-auto text-graphite/50" aria-hidden="true" />
              <p className="mt-2 text-sm font-semibold text-encre">{rechercheActive ? 'Aucun dossier archivé ne correspond.' : 'Aucun dossier archivé.'}</p>
              <p className="text-xs text-graphite">Un dossier archivé depuis sa fiche ou la liste des dossiers apparaît ici.</p>
            </div>
          ) : etroit ? (
            <div data-testid="cartes-archives">
              <label className="flex items-center gap-3 px-3 min-h-11 border-b border-trait text-sm text-graphite cursor-pointer select-none">
                <input type="checkbox" checked={toutSelectionne} onChange={basculerTout} className="w-5 h-5 rounded border-trait text-blue-600" />
                Tout sélectionner sur cette page
              </label>
              <ul className="divide-y divide-trait" aria-label="Dossiers archivés">
                {page.data.map(t => (
                  <li key={t.id} className={`rail rail-ferme flex items-stretch ${selection.has(t.id) ? 'bg-sapin-50' : ''}`}>
                    <label className="flex items-start justify-center w-11 pt-3.5 flex-shrink-0 cursor-pointer">
                      <input type="checkbox" checked={selection.has(t.id)} onChange={() => basculer(t.id)}
                        aria-label={`Sélectionner le dossier ${reference(t)}`} className="w-5 h-5 rounded border-trait text-blue-600" />
                    </label>
                    <div className="flex-1 min-w-0 py-3 pr-3">
                      <button type="button" onClick={() => onOuvrirDossier(t.id)} className="w-full text-left min-h-11">
                        <span className="flex items-center gap-2">
                          <span className="ref text-sm">{reference(t)}</span>
                          <BadgeHorsSav dossier={t} />
                        </span>
                        <span className="block text-sm font-medium text-encre line-clamp-2 mt-1 break-words">{t.title}</span>
                        <span className="block text-xs text-graphite mt-0.5 truncate">{nomClient(t) || 'Client non renseigné'}{emailClient(t) ? ` · ${emailClient(t)}` : ''}</span>
                        <span className="block text-xs text-graphite mt-0.5">Fermé le <span className="tnum">{formatDateTime(dateFermeture(t))}</span></span>
                      </button>
                      <div className="mt-2">{boutonDesarchiver(t)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white border-b border-trait">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <input type="checkbox" checked={toutSelectionne} onChange={basculerTout} aria-label="Tout sélectionner sur cette page" className="w-4 h-4 rounded border-trait text-blue-600" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Dossier</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Fermé le</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-graphite"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-trait">
                  {page.data.map(t => (
                    <tr key={t.id} onClick={() => onOuvrirDossier(t.id)}
                      className={`rail rail-ferme hover:bg-brume transition-colors cursor-pointer ${selection.has(t.id) ? 'bg-sapin-50' : ''}`}>
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selection.has(t.id)} onChange={() => basculer(t.id)}
                          aria-label={`Sélectionner le dossier ${reference(t)}`} className="w-4 h-4 rounded border-trait text-blue-600" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="ref text-sm">{reference(t)}</span>
                          <BadgeHorsSav dossier={t} />
                        </div>
                        <p className="text-sm font-medium text-encre line-clamp-1 max-w-sm mt-1">{t.title}</p>
                      </td>
                      <td className="px-4 py-3">
                        {nomClient(t) ? <p className="text-sm font-medium text-encre">{nomClient(t)}</p> : <span className="text-sm text-graphite/70">-</span>}
                        {emailClient(t) && <p className="text-xs text-graphite truncate max-w-[220px]">{emailClient(t)}</p>}
                      </td>
                      <td className="px-4 py-3"><BadgeStatut status={t.status} /></td>
                      <td className="px-4 py-3 text-sm text-encre tnum whitespace-nowrap">{formatDateTime(dateFermeture(t))}</td>
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>{boutonDesarchiver(t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {page.total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white border-t border-trait">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-sm text-encre">
                  Affichage <span className="font-medium tnum">{debutAffiche}</span> - <span className="font-medium tnum">{finAffichee}</span> sur <span className="font-medium tnum">{page.total}</span>
                </span>
                <select value={etat.parPage} onChange={e => setEtat({ parPage: Number(e.target.value), page: 1 })} aria-label="Dossiers par page"
                  className="text-sm border border-trait rounded-lg px-2 py-1 max-lg:min-h-11 max-sm:text-base">
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
              </div>
              <nav className="flex items-center gap-2 max-sm:w-full max-sm:justify-between" aria-label="Pagination des archives">
                <button onClick={() => setEtat({ page: etat.page - 1 })} disabled={etat.page <= 1} aria-label="Page précédente"
                  className="w-10 h-10 max-lg:w-11 max-lg:h-11 inline-flex items-center justify-center rounded-lg border border-trait text-graphite hover:bg-brume disabled:opacity-50 disabled:cursor-not-allowed">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-encre tnum" aria-current="page">Page <span className="font-semibold">{etat.page}</span> sur {Math.max(1, page.totalPages)}</span>
                <button onClick={() => setEtat({ page: etat.page + 1 })} disabled={etat.page >= page.totalPages} aria-label="Page suivante"
                  className="w-10 h-10 max-lg:w-11 max-lg:h-11 inline-flex items-center justify-center rounded-lg border border-trait text-graphite hover:bg-brume disabled:opacity-50 disabled:cursor-not-allowed">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </nav>
            </div>
          )}
        </Card>
      </div>

      <FenetreDesarchivage
        ouvert={aDesarchiver !== null}
        nombre={aDesarchiver?.length ?? 1}
        onFermer={() => setADesarchiver(null)}
        onConfirmer={() => desarchiver(aDesarchiver ?? [])}
      />
    </div>
  );
};

export default ArchivesView;
